// A subagent's connections from its own page (a workspace with Connections
// on): what Lattice thinks it needs and does not have yet (at most 5, each
// with one line why), what Lattice gave it by itself when it was made (with
// Undo), and one call that gives it a whole connection (both sides; its
// Tools switch on when the connection has tools, Run scripts never) or
// takes it away. Routes: routes/subagent_connections.py. A workspace with
// Connections off answers 409 `connections_disabled`, and a server with the
// directory switched off 404 `directory_off`: both read as null (nothing to
// show), never as an error.

import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";

import { ApiError, apiFetch, errorCode } from "@/lib/api";
import type { components } from "@/types/api.gen";

import { connectionsKey } from "./connections";
import { dataSourceKeys } from "./dataSources";
import { blueprintKeys } from "./subagentBlueprints";
import { subagentDataKeys } from "./subagentData";

type RawSuggestions = components["schemas"]["SubagentSuggestions"];
type RawSuggestion = components["schemas"]["ConnectionSuggestion"];
type RawGiven = components["schemas"]["AutoGiven"];
type RawChange = components["schemas"]["SubagentEntryChange"];

export type Side = "data" | "tools";

/** One record Add gives (a data source's name, a connection's id, a REST API's or MCP server's name). */
export interface SuggestedMember {
  store: "connection" | "data" | "rest" | "mcp";
  name: string;
  side: Side;
}

export type ConnectionSuggestion = Required<Pick<RawSuggestion, "entry_id" | "name" | "why">> & {
  sides: Side[];
  members: SuggestedMember[];
  strong: boolean;
  needs_run_scripts: boolean;
  problem: string;
};

export type AutoGiven = Required<Pick<RawGiven, "entry_id" | "name" | "why">> & {
  sides: Side[];
  origin: string;
  at: string;
  data_sources: string[];
  connections: string[];
};

export interface SubagentSuggestions {
  template: string;
  suggestions: ConnectionSuggestion[];
  given: AutoGiven[];
  auto_on: boolean;
}

export type SubagentEntryChange = RawChange;

export function normalizeGiven(g: RawGiven): AutoGiven {
  return {
    entry_id: g.entry_id,
    name: g.name,
    why: g.why,
    sides: (g.sides ?? []) as Side[],
    origin: g.origin ?? "",
    at: g.at ?? "",
    data_sources: g.data_sources ?? [],
    connections: g.connections ?? [],
  };
}

export function normalizeSuggestions(raw: RawSuggestions): SubagentSuggestions {
  return {
    template: raw.template,
    suggestions: (raw.suggestions ?? []).map((s) => ({
      entry_id: s.entry_id,
      name: s.name,
      why: s.why,
      sides: (s.sides ?? []) as Side[],
      members: (s.members ?? []).map((m) => ({ store: m.store, name: m.name, side: m.side })),
      strong: s.strong ?? false,
      needs_run_scripts: s.needs_run_scripts ?? false,
      problem: s.problem ?? "",
    })),
    given: (raw.given ?? []).map(normalizeGiven),
    auto_on: raw.auto_on ?? true,
  };
}

const base = (c: string, t: string) => `/api/customers/${encodeURIComponent(c)}/subagent-blueprints/${encodeURIComponent(t)}`;

export const subagentConnectionKeys = {
  suggested: (c: string | null, t: string | null) => ["subagent-suggested-connections", c, t] as const,
};

/** Nothing to show (Connections off, or the directory off on this server). */
const quiet = (e: unknown) => e instanceof ApiError && ["connections_disabled", "directory_off"].includes(errorCode(e) ?? "");

/** Suggested for it, and what Lattice gave it by itself; null where there is nothing to show. `enabled` = Connections on. */
export function useSuggestedConnections(customerId: string | null, template: string | null, enabled = true) {
  return useQuery({
    queryKey: subagentConnectionKeys.suggested(customerId, template),
    enabled: enabled && !!customerId && !!template,
    queryFn: async ({ signal }) => {
      try {
        return normalizeSuggestions(await apiFetch<RawSuggestions>(`${base(customerId!, template!)}/suggested-connections`, { signal }));
      } catch (e) {
        if (quiet(e)) return null;
        throw e;
      }
    },
    staleTime: 10_000,
    retry: (count, error) => !(error instanceof ApiError && error.status < 500) && count < 1,
  });
}

/** What a give or a take moves: who holds what, its Tools switch, the lists the page reads. */
function invalidate(qc: QueryClient, customerId: string, template: string) {
  void qc.invalidateQueries({ queryKey: subagentConnectionKeys.suggested(customerId, template) });
  void qc.invalidateQueries({ queryKey: subagentDataKeys.one(customerId, template) });
  void qc.invalidateQueries({ queryKey: subagentDataKeys.connections(customerId, template) });
  void qc.invalidateQueries({ queryKey: blueprintKeys.one(customerId, template) });
  void qc.invalidateQueries({ queryKey: connectionsKey(customerId) });
  void qc.invalidateQueries({ queryKey: dataSourceKeys.all(customerId) });
  // the connections key above covers the directory too (["connections", c, "directory"])
  void qc.invalidateQueries({ queryKey: ["integrations", customerId] });
}

/** Give it a whole connection (both sides unless `sides`); saved at once. */
export function useGiveConnection() {
  const qc = useQueryClient();
  return useMutation({
    meta: { errorHandled: true },
    mutationFn: ({ customerId, template, entryId, sides }: { customerId: string; template: string; entryId: string; sides?: Side[] }) =>
      apiFetch<RawChange>(`${base(customerId, template)}/connections/${encodeURIComponent(entryId)}`, {
        method: "POST",
        body: sides?.length ? { sides } : {},
      }),
    onSuccess: (_d, v) => invalidate(qc, v.customerId, v.template),
  });
}

/** Take its own grants on a connection away; `undo` = Lattice's own gift taken back (never suggested again). */
export function useTakeConnection() {
  const qc = useQueryClient();
  return useMutation({
    meta: { errorHandled: true },
    mutationFn: ({ customerId, template, entryId, undo }: { customerId: string; template: string; entryId: string; undo?: boolean }) =>
      apiFetch<RawChange>(`${base(customerId, template)}/connections/${encodeURIComponent(entryId)}${undo ? "?undo=true" : ""}`, {
        method: "DELETE",
      }),
    onSuccess: (_d, v) => invalidate(qc, v.customerId, v.template),
  });
}
